import { createSvgEl, type CartesianDomain } from '@learnforge/demo-render';

export interface ParabolaSketchOptions {
  a: number;
  b: number;
  c: number;
  width: number;
  height: number;
  xDomain?: [number, number];
  yDomain?: [number, number];
}

export interface ParabolaSketchHandle {
  group: SVGGElement;
  update(opts: Partial<ParabolaSketchOptions>): void;
  destroy(): void;
}

const SAMPLE_COUNT = 80;
const WINDOW_PADDING = 1.3;
const AXIS_MARGIN = 6;
let parabolaClipId = 0;

function fittedXDomain(a: number, b: number, c: number): [number, number] {
  const xv = a === 0 ? 0 : -b / (2 * a);
  const discriminant = b * b - 4 * a * c;
  const hasRoots = a !== 0 && discriminant >= 0;
  let r1 = 0;
  let r2 = 0;
  if (hasRoots) {
    const sqrtDelta = Math.sqrt(discriminant);
    r1 = (-b + sqrtDelta) / (2 * a);
    r2 = (-b - sqrtDelta) / (2 * a);
  }
  const span = hasRoots ? Math.max(2, Math.abs(r1 - xv), Math.abs(r2 - xv)) : 3;
  return [xv - WINDOW_PADDING * span, xv + WINDOW_PADDING * span];
}

function fittedYDomain(
  a: number,
  b: number,
  c: number,
  xDomain: [number, number],
): [number, number] {
  const ys = Array.from({ length: SAMPLE_COUNT }, (_, index) => {
    const x = xDomain[0] + ((xDomain[1] - xDomain[0]) * index) / (SAMPLE_COUNT - 1);
    return a * x * x + b * x + c;
  });
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  if (yMax === yMin) return [yMin - 1, yMax + 1];
  const pad = (yMax - yMin) * 0.1;
  return [yMin - pad, yMax + pad];
}

export function fitParabolaDomains(a: number, b: number, c: number): CartesianDomain {
  const xDomain = fittedXDomain(a, b, c);
  return { xDomain, yDomain: fittedYDomain(a, b, c, xDomain) };
}

/**
 * Pick a "nice" tick step (1, 2, or 5 times a power of ten) so that a span
 * fits roughly targetTicks ticks.
 */
export function niceTickStep(span: number, targetTicks: number): number {
  const raw = span / targetTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const mult of [1, 2, 5, 10]) {
    if (mult * magnitude >= raw) return mult * magnitude;
  }
  return 10 * magnitude;
}

export function createParabolaSketch(
  parent: SVGSVGElement | SVGGElement,
  opts: ParabolaSketchOptions,
): ParabolaSketchHandle {
  const group = createSvgEl('g');
  parent.appendChild(group);
  const clipId = `alg-parabola-clip-${parabolaClipId++}`;

  const currentOpts: ParabolaSketchOptions = { ...opts };

  function render(): void {
    while (group.firstChild) {
      group.removeChild(group.firstChild);
    }

    const { a, b, c, width, height } = currentOpts;
    const defs = createSvgEl('defs');
    const clipPath = createSvgEl('clipPath', { id: clipId });
    clipPath.appendChild(
      createSvgEl('rect', {
        x: AXIS_MARGIN,
        y: AXIS_MARGIN,
        width: width - 2 * AXIS_MARGIN,
        height: height - 2 * AXIS_MARGIN,
      }),
    );
    defs.appendChild(clipPath);
    const content = createSvgEl('g', {
      'data-role': 'parabola-content',
      'clip-path': `url(#${clipId})`,
    });
    group.append(defs, content);

    // The viewport owns the visible domains. Without explicit domains, retain
    // the original fitted view for callers that only need a static sketch.
    const xv = a === 0 ? 0 : -b / (2 * a);
    const discriminant = b * b - 4 * a * c;
    const hasRoots = a !== 0 && discriminant >= 0;
    let r1 = 0;
    let r2 = 0;
    if (hasRoots) {
      const sqrtDelta = Math.sqrt(discriminant);
      r1 = (-b + sqrtDelta) / (2 * a);
      r2 = (-b - sqrtDelta) / (2 * a);
    }
    const fitted = fitParabolaDomains(a, b, c);
    const [xMin, xMax] = currentOpts.xDomain ?? fitted.xDomain;

    // Resample the curve across the current visible x-domain.
    const xs: number[] = [];
    const ys: number[] = [];
    const sampleCount = Math.max(
      SAMPLE_COUNT,
      Math.round((width * (globalThis.devicePixelRatio || 1)) / 2),
    );
    for (let i = 0; i < sampleCount; i++) {
      const x = xMin + ((xMax - xMin) * i) / (sampleCount - 1);
      const y = a * x * x + b * x + c;
      xs.push(x);
      ys.push(y);
    }

    const [yMin, yMax] = currentOpts.yDomain ?? fittedYDomain(a, b, c, [xMin, xMax]);

    const mapX = (x: number): number =>
      AXIS_MARGIN + ((x - xMin) / (xMax - xMin)) * (width - 2 * AXIS_MARGIN);
    const mapY = (y: number): number =>
      height - AXIS_MARGIN - ((y - yMin) / (yMax - yMin)) * (height - 2 * AXIS_MARGIN);

    const xStep = niceTickStep(xMax - xMin, 6);
    const yStep = niceTickStep(yMax - yMin, 4);
    const fmt = (v: number): string => {
      const rounded = Math.round(v * 100) / 100;
      return Object.is(rounded, -0) ? '0' : rounded.toString();
    };

    // Vertical gridlines + x tick labels — iterate by INDEX, not by accumulating
    // x += xStep (float error breaks fractional steps like 0.2).
    const yZeroTick = yMin <= 0 && 0 <= yMax ? mapY(0) : null;
    const firstXi = Math.ceil(xMin / xStep);
    for (let i = firstXi; i * xStep <= xMax + 1e-9; i += 1) {
      const x = i * xStep;
      const px = mapX(x);
      content.appendChild(
        createSvgEl('line', { x1: px, y1: 0, x2: px, y2: height, class: 'alg-gridline' }),
      );
      if (Math.abs(x) < xStep / 2) continue; // skip crowding the origin
      const labelY = yZeroTick === null ? height - 4 : Math.min(yZeroTick + 14, height - 4);
      const label = createSvgEl('text', {
        x: px,
        y: labelY,
        'text-anchor': 'middle',
        class: 'alg-text-mute',
        'data-axis': 'x',
      });
      label.textContent = fmt(x);
      content.appendChild(label);
    }

    // Horizontal gridlines + y tick labels (same index iteration)
    const xZeroTick = xMin <= 0 && 0 <= xMax ? mapX(0) : null;
    const firstYi = Math.ceil(yMin / yStep);
    for (let i = firstYi; i * yStep <= yMax + 1e-9; i += 1) {
      const y = i * yStep;
      const py = mapY(y);
      content.appendChild(
        createSvgEl('line', { x1: 0, y1: py, x2: width, y2: py, class: 'alg-gridline' }),
      );
      if (Math.abs(y) < yStep / 2) continue;
      const labelX = xZeroTick === null ? 4 : Math.max(xZeroTick - 6, 4);
      const anchor = xZeroTick === null ? 'start' : 'end';
      const label = createSvgEl('text', {
        x: labelX,
        y: py + 4,
        'text-anchor': anchor,
        class: 'alg-text-mute',
        'data-axis': 'y',
      });
      label.textContent = fmt(y);
      content.appendChild(label);
    }

    // x-axis (y = 0), only if 0 is inside the y range
    if (yMin <= 0 && 0 <= yMax) {
      const yZero = mapY(0);
      const xAxis = createSvgEl('line', {
        x1: 0,
        y1: yZero,
        x2: width,
        y2: yZero,
        class: 'alg-sketch-axis',
      });
      content.appendChild(xAxis);
    }

    // y-axis (x = 0), only if 0 is inside the x range
    if (xMin <= 0 && 0 <= xMax) {
      const xZero = mapX(0);
      const yAxis = createSvgEl('line', {
        x1: xZero,
        y1: 0,
        x2: xZero,
        y2: height,
        class: 'alg-sketch-axis',
      });
      content.appendChild(yAxis);
    }

    // Sampled curve
    const points = xs.map((x, i) => `${mapX(x)},${mapY(ys[i]!)}`).join(' ');
    const curve = createSvgEl('polyline', {
      points,
      class: 'alg-sketch-curve',
    });
    content.appendChild(curve);

    // Root markers (on the x-axis)
    if (hasRoots) {
      for (const root of [r1, r2]) {
        const point = createSvgEl('circle', {
          cx: mapX(root),
          cy: mapY(0),
          r: 3,
          class: 'alg-point',
          'data-role': 'quadratic-root',
        });
        content.appendChild(point);
      }
    }

    // Vertex marker — only meaningful when the curve is actually a parabola
    if (a !== 0) {
      const yv = a * xv * xv + b * xv + c;
      const vertex = createSvgEl('circle', {
        cx: mapX(xv),
        cy: mapY(yv),
        r: 3,
        class: 'alg-point',
        'data-role': 'quadratic-vertex',
      });
      content.appendChild(vertex);
    }
  }

  render();

  const handle: ParabolaSketchHandle = {
    group,

    update(partial: Partial<ParabolaSketchOptions>): void {
      Object.assign(currentOpts, partial);
      render();
    },

    destroy(): void {
      if (group.parentNode) {
        group.parentNode.removeChild(group);
      }
    },
  };

  return handle;
}
